import { BEATS, partForBeat, type BeatEntry } from '@/story/beats';
import { OFFICE_PART_MODULE } from '@/game/beats/office';
import { DAWN_TRANSPORT_PART_MODULE } from '@/game/beats/dawnTransport';
import { IT_FAILS_PART_MODULE } from '@/game/beats/itFails';
import { ENDING_PART_MODULE } from '@/game/beats/ending';
import { TWELVE_OUT_PART_MODULE } from '@/game/beats/twelveOut';
import { OUT_PART_MODULE } from '@/game/beats/out';
import { WOOL_SHED_PUZZLE_PART_MODULE, WOOL_SHED_REST_PART_MODULE } from '@/game/beats/woolShed';
import { BLACKOUT_RETURN_PART_MODULE } from '@/game/beats/blackoutReturn';
import { BLACKOUT_HALL_PART_MODULE } from '@/game/beats/blackoutHall';
import { OTTO_PART_MODULE } from '@/game/beats/otto';
import { SERVICE_CORRIDOR_PART_MODULE } from '@/game/beats/serviceCorridor';
import { WEAKEST_LATCH_PART_MODULE } from '@/game/beats/weakestLatch';
import { CULL_CORRIDOR_PART_MODULE } from '@/game/beats/cullCorridor';
import { BORN_PART_MODULE } from '@/game/beats/born';
import { LOADING_YARD_PART_MODULE } from '@/game/beats/loadingYard';
import type { BeatPartModuleDefinition } from '@/game/beatPartModule';

/** The explicit registry. Registering a migrated part is one line here; nothing imports for effect. */
export const BEAT_PART_MODULES: readonly BeatPartModuleDefinition[] = [
  OFFICE_PART_MODULE, WOOL_SHED_PUZZLE_PART_MODULE, WOOL_SHED_REST_PART_MODULE, TWELVE_OUT_PART_MODULE, DAWN_TRANSPORT_PART_MODULE,
  IT_FAILS_PART_MODULE, ENDING_PART_MODULE, OUT_PART_MODULE, BLACKOUT_RETURN_PART_MODULE,
  BLACKOUT_HALL_PART_MODULE, OTTO_PART_MODULE, SERVICE_CORRIDOR_PART_MODULE, WEAKEST_LATCH_PART_MODULE,
  CULL_CORRIDOR_PART_MODULE, BORN_PART_MODULE, LOADING_YARD_PART_MODULE,
];

/**
 * Beat 0 is the hall with no beat (what most specs boot); it has no story to own.
 *
 * Every campaign part must be registered above OR listed here, never both and never neither.
 * That is what makes a dropped registration fail at load instead of booting the room with no
 * beat — the office would otherwise open lit, inert and unfinishable, with nothing in the
 * console. (Until session 85 a third list held the parts still wired by branches in `main.ts`;
 * D-235 emptied it and it is gone.)
 */
export const STORYLESS_PARTS: readonly string[] = ['0/1'];

export function beatPartModuleFor(beat: number, part: number, room: string): BeatPartModuleDefinition | null {
  return BEAT_PART_MODULES.find((entry) => entry.beat === beat
    && entry.part === part && entry.room === room) ?? null;
}

/**
 * Pure and cheap: it reads declarations and constructs nothing. Module construction is validated
 * at boot, by `validateBeatPartModule`, when the host actually creates the one it runs — a factory
 * may build scene objects, and doing that for every registered part on import is not free.
 */
export function validateBeatPartModuleRegistry(
  list: readonly BeatPartModuleDefinition[],
  beats: Readonly<Record<number, BeatEntry>> = BEATS,
  storyless: readonly string[] = STORYLESS_PARTS,
): void {
  const registered = new Set<string>();
  for (const definition of list) {
    const context = `beat ${definition.beat} / part ${definition.part} / ${definition.room}`;
    const key = `${definition.beat}/${definition.part}`;
    if (registered.has(key)) throw new Error(`${context}: duplicate module registration`);
    registered.add(key);
    const beat = beats[definition.beat];
    if (beat === undefined) throw new Error(`${context}: unknown beat`);
    const part = partForBeat(beat, definition.part);
    if (part === null) throw new Error(`${context}: unknown part`);
    if (part.room !== definition.room) {
      throw new Error(`${context}: campaign room is "${part.room}"`);
    }
  }
  for (const [beatKey, entry] of Object.entries(beats)) {
    entry.parts.forEach((part, index) => {
      const key = `${beatKey}/${index + 1}`;
      const owners = Number(registered.has(key)) + Number(storyless.includes(key));
      if (owners === 0) {
        throw new Error(`beat ${beatKey} / part ${index + 1} / ${part.room}: no module registered`);
      }
      if (owners > 1) {
        throw new Error(`beat ${beatKey} / part ${index + 1} / ${part.room}: owned by more than one wiring path`);
      }
    });
  }
  for (const key of storyless) {
    const [beat, part] = key.split('/').map(Number);
    const entry = beat === undefined ? undefined : beats[beat];
    if (entry === undefined || part === undefined || partForBeat(entry, part) === null) {
      throw new Error(`beat part "${key}" is listed but not in campaign metadata`);
    }
  }
}

validateBeatPartModuleRegistry(BEAT_PART_MODULES);
